import { Button, Input, useToast } from '@orbind/ui'
import { montarUrlDoBotao, normalizarUrl, rotuloDaUrl } from '@orbind/core'
import { useEffect, useRef, useState } from 'react'
import { abrirAnexoDeCampo, uploadCampoAnexo, uploadWorkItemImage, type AnexoDeCampo } from './api.ts'
import './campos-tipos.css'

/**
 * Campos complementares que precisam de mais do que um `<input>`: link, imagem
 * e botão (SPEC-20260828-1914).
 *
 * Ficam separados do `campos-shared` porque cada um carrega estado próprio
 * (envio em andamento, visualização ampliada) e o arquivo compartilhado é só o
 * roteador de tipos.
 */

/* ------------------------------------------------------------------- link */

/**
 * Campo de link: valida o endereço e abre a partir do próprio campo.
 *
 * O endereço é normalizado só ao sair do campo — normalizar a cada tecla
 * transformaria "or" em "https://or" no meio da digitação.
 */
export function CampoLink({
  value,
  onChange,
  onCommit,
}: {
  value: string
  onChange: (v: string) => void
  onCommit: (v: string) => void
}) {
  const alvo = normalizarUrl(value)
  const invalido = value.trim() !== '' && !alvo
  return (
    <div className="cft__link">
      <Input
        value={value}
        placeholder="orbind.com.br/planos"
        inputMode="url"
        invalid={invalido}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          const normal = normalizarUrl(value)
          onCommit(normal ?? value)
        }}
      />
      <a
        className={`cft__abrir${alvo ? '' : ' cft__abrir--off'}`}
        href={alvo ?? undefined}
        target="_blank"
        rel="noreferrer noopener"
        aria-disabled={!alvo}
        title={alvo ? `Abrir ${rotuloDaUrl(value)}` : 'Informe um endereço válido para abrir'}
        onClick={(e) => {
          if (!alvo) e.preventDefault()
        }}
      >
        <i className="fas fa-arrow-up-right-from-square" aria-hidden />
        <span className="ob-sr-only">Abrir link</span>
      </a>
      {invalido ? <p className="cft__erro">Endereço inválido.</p> : null}
    </div>
  )
}

/* ------------------------------------------------------------------ botão */

/**
 * Campo botão: o endereço vem da configuração do campo, não do registro, e
 * pode citar outros campos entre chaves. Sem endereço válido o botão fica
 * desligado com o motivo no title — some seria pior, o admin não descobriria.
 */
export function CampoBotao({
  template,
  rotulo,
  valores,
}: {
  template: string
  rotulo: string
  valores: Record<string, unknown>
}) {
  const alvo = montarUrlDoBotao(template, valores)
  return (
    <Button
      variant="secondary"
      icon="fas fa-external-link-alt"
      disabled={!alvo}
      title={alvo ?? 'Configure um endereço válido para este botão.'}
      onClick={() => {
        if (alvo) window.open(alvo, '_blank', 'noopener,noreferrer')
      }}
    >
      {rotulo || 'Abrir'}
    </Button>
  )
}

/* ----------------------------------------------------------------- imagem */

/** Visualização ampliada com zoom e arraste. Fecha no Esc ou clicando fora. */
function Ampliada({ src, alt, onFechar }: { src: string; alt: string; onFechar: () => void }) {
  const [escala, setEscala] = useState(1)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const arrasto = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar()
      if (e.key === '+' || e.key === '=') setEscala((s) => Math.min(8, s * 1.25))
      if (e.key === '-') setEscala((s) => Math.max(0.2, s / 1.25))
    }
    window.addEventListener('keydown', tecla)
    return () => window.removeEventListener('keydown', tecla)
  }, [onFechar])

  const zoom = (fator: number) => setEscala((s) => Math.min(8, Math.max(0.2, s * fator)))

  return (
    <div className="cft__lupa" role="dialog" aria-modal="true" aria-label={`Imagem ampliada: ${alt}`}>
      <div className="cft__lupa-fundo" onClick={onFechar} />
      <div className="cft__lupa-barra">
        <button type="button" onClick={() => zoom(1 / 1.25)} title="Reduzir (-)" aria-label="Reduzir">
          <i className="fas fa-minus" aria-hidden />
        </button>
        <span className="cft__lupa-nivel">{Math.round(escala * 100)}%</span>
        <button type="button" onClick={() => zoom(1.25)} title="Ampliar (+)" aria-label="Ampliar">
          <i className="fas fa-plus" aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => {
            setEscala(1)
            setPos({ x: 0, y: 0 })
          }}
          title="Tamanho original"
          aria-label="Tamanho original"
        >
          <i className="fas fa-compress" aria-hidden />
        </button>
        <a href={src} target="_blank" rel="noreferrer noopener" title="Abrir em outra aba" aria-label="Abrir em outra aba">
          <i className="fas fa-arrow-up-right-from-square" aria-hidden />
        </a>
        <button type="button" onClick={onFechar} title="Fechar (Esc)" aria-label="Fechar">
          <i className="fas fa-times" aria-hidden />
        </button>
      </div>
      <div
        className="cft__lupa-palco"
        // a roda do mouse dá o zoom fino; sem isso só sobrariam os botões
        onWheel={(e) => zoom(e.deltaY < 0 ? 1.12 : 1 / 1.12)}
        onPointerDown={(e) => {
          arrasto.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }
          ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
        }}
        onPointerMove={(e) => {
          if (!arrasto.current) return
          setPos({ x: e.clientX - arrasto.current.x, y: e.clientY - arrasto.current.y })
        }}
        onPointerUp={(e) => {
          arrasto.current = null
          ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
        }}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${escala})` }}
        />
      </div>
    </div>
  )
}

/**
 * Campo de imagem: envia para o mesmo cofre das imagens do item (R2 quando
 * está ligado) e guarda só a URL pública no valor do campo.
 */
export function CampoImagem({
  value,
  rotulo,
  onChange,
}: {
  value: string
  rotulo: string
  onChange: (v: string) => void
}) {
  const toast = useToast()
  const [enviando, setEnviando] = useState(false)
  const [ampliada, setAmpliada] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  const enviar = async (arquivo: File | undefined) => {
    if (!arquivo) return
    if (!arquivo.type.startsWith('image/')) {
      toast.push({ tone: 'danger', message: 'Escolha um arquivo de imagem.' })
      return
    }
    setEnviando(true)
    try {
      onChange(await uploadWorkItemImage(arquivo))
    } catch {
      toast.push({ tone: 'danger', message: 'Não foi possível enviar a imagem.' })
    } finally {
      setEnviando(false)
      if (input.current) input.current.value = ''
    }
  }

  return (
    <div
      className="cft__img"
      // arrastar o arquivo direto para o campo é o caminho mais curto
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) e.preventDefault()
      }}
      onDrop={(e) => {
        if (!e.dataTransfer.files?.length) return
        e.preventDefault()
        void enviar(e.dataTransfer.files[0])
      }}
    >
      <input
        ref={input}
        type="file"
        accept="image/*"
        className="cft__img-input"
        onChange={(e) => void enviar(e.target.files?.[0])}
      />
      {value ? (
        <div className="cft__img-tem">
          <button
            type="button"
            className="cft__img-thumb"
            title="Ampliar"
            aria-label={`Ampliar imagem de ${rotulo}`}
            onClick={() => setAmpliada(true)}
          >
            <img src={value} alt={rotulo} />
            <span className="cft__img-lupa" aria-hidden>
              <i className="fas fa-search-plus" />
            </span>
          </button>
          <div className="cft__img-acoes">
            <button type="button" onClick={() => input.current?.click()} disabled={enviando}>
              {enviando ? 'Enviando…' : 'Trocar'}
            </button>
            <button type="button" className="cft__img-remover" onClick={() => onChange('')}>
              Remover
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="cft__img-vazio" onClick={() => input.current?.click()} disabled={enviando}>
          <i className="far fa-image" aria-hidden />
          <span>{enviando ? 'Enviando…' : 'Escolher imagem ou arrastar aqui'}</span>
        </button>
      )}
      {ampliada && value ? <Ampliada src={value} alt={rotulo} onFechar={() => setAmpliada(false)} /> : null}
    </div>
  )
}

/* ------------------------------------------------------------------ anexo */

function tamanhoDoAnexo(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Campo Anexo (SPEC-20261104-1542): um arquivo por campo, guardado no R2; o
 * valor é { key, name, mimeType, sizeBytes } e o nome vira um link que abre o
 * arquivo (PDF/imagem numa aba; o resto baixa). Mesmo desenho do campo de
 * imagem: vazio = área pontilhada que aceita clique ou arrastar.
 */
export function CampoAnexo({
  value,
  rotulo,
  onChange,
}: {
  value: AnexoDeCampo | null
  rotulo: string
  onChange: (v: AnexoDeCampo | null) => void
}) {
  const toast = useToast()
  const [enviando, setEnviando] = useState(false)
  const [abrindo, setAbrindo] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  const enviar = async (arquivo: File | undefined) => {
    if (!arquivo) return
    setEnviando(true)
    try {
      onChange(await uploadCampoAnexo(arquivo))
    } catch (e) {
      toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível enviar o anexo.' })
    } finally {
      setEnviando(false)
      if (input.current) input.current.value = ''
    }
  }

  const abrir = async () => {
    if (!value) return
    setAbrindo(true)
    try {
      await abrirAnexoDeCampo(value)
    } catch {
      toast.push({ tone: 'danger', message: 'Não foi possível abrir o anexo.' })
    } finally {
      setAbrindo(false)
    }
  }

  return (
    <div
      className="cft__img cft__arq"
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) e.preventDefault()
      }}
      onDrop={(e) => {
        if (!e.dataTransfer.files?.length) return
        e.preventDefault()
        void enviar(e.dataTransfer.files[0])
      }}
    >
      <input ref={input} type="file" className="cft__img-input" onChange={(e) => void enviar(e.target.files?.[0])} />
      {value ? (
        <div className="cft__img-tem cft__arq-tem">
          <button
            type="button"
            className="cft__arq-link"
            title={`Abrir ${value.name}`}
            aria-label={`Abrir anexo de ${rotulo}: ${value.name}`}
            disabled={abrindo}
            onClick={() => void abrir()}
          >
            <i className="fas fa-paperclip" aria-hidden />
            <span className="cft__arq-nome">{value.name}</span>
            <span className="cft__arq-tamanho">{abrindo ? 'Abrindo…' : tamanhoDoAnexo(value.sizeBytes)}</span>
          </button>
          <div className="cft__img-acoes">
            <button type="button" onClick={() => input.current?.click()} disabled={enviando}>
              {enviando ? 'Enviando…' : 'Trocar'}
            </button>
            <button type="button" className="cft__img-remover" onClick={() => onChange(null)}>
              Remover
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="cft__img-vazio" onClick={() => input.current?.click()} disabled={enviando}>
          <i className="fas fa-paperclip" aria-hidden />
          <span>{enviando ? 'Enviando…' : 'Escolher arquivo ou arrastar aqui'}</span>
        </button>
      )}
    </div>
  )
}
